/**
 * Sentry Service
 * 
 * Initializes Sentry for error tracking and provides utilities
 * for manual error reporting with game context.
 */
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';

// Check if Sentry is initialized
let isInitialized = false;

/**
 * Initialize Sentry with the configured DSN
 * Should be called once at app startup, before React renders
 */
export const initSentry = () => {
    if (isInitialized) {
        console.warn('[SentryService] Already initialized');
        return;
    }

    const dsn = process.env.REACT_APP_SENTRY_DSN;

    if (!dsn) {
        console.warn('[SentryService] No DSN configured, Sentry will not be initialized');
        return;
    }

    const tracesSampleRate = parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE) || 0.1;

    try {
        Sentry.init({
            dsn,
            tracesSampleRate,
            environment: process.env.NODE_ENV || 'development',
            release: `rondaybaciga@${process.env.REACT_APP_VERSION || '1.0.0'}`,

            // Transport configuration for better reliability
            transportOptions: {
                // Increase timeout for slow connections
                fetchOptions: {
                    timeout: 10000,
                },
            },

            // Additional configuration
            beforeSend(event) {
                // Remove any potentially sensitive data
                if (event.user) {
                    delete event.user.ip_address;
                    delete event.user.email;
                }
                return event;
            },

            // Add platform info
            tags: {
                platform: Capacitor.isNativePlatform() ? 'native' : 'web',
                nativePlatform: Capacitor.getPlatform(),
            },
        });

        isInitialized = true;
        debugLog('[SentryService] Initialized successfully');
    } catch (error) {
        console.error('[SentryService] Failed to initialize:', error);
    }
};

/**
 * Send a manual error report to Sentry with game context
 *
 * @param {Object} context - Context data to include in the report
 * @param {Array} context.logs - Array of game log events
 * @param {Object} context.gameState - Current game state (serializable)
 * @param {string} context.currentScreen - Current screen/route name
 * @param {string} context.gamePhase - Current game phase
 * @param {Object} context.screenSize - { width, height }
 * @param {string} context.userMessage - Optional user-provided description of the issue
 * @returns {Promise<boolean>} - True if report was sent successfully
 */
export const sendErrorReport = async ({ logs, gameState, currentScreen, gamePhase, screenSize, userMessage }) => {
    if (!isInitialized) {
        console.warn('[SentryService] Not initialized, cannot send report');
        return false;
    }

    try {
        // Create a scope for this specific report
        Sentry.withScope((scope) => {
            // Add user message if provided
            if (userMessage && userMessage.trim()) {
                scope.setContext('User Report', {
                    message: userMessage,
                    timestamp: new Date().toISOString(),
                });
            }

            // Add game logs as attachment or extra data
            scope.setExtra('gameLogs', logs || []);

            // Add game state (sanitized)
            if (gameState) {
                // Remove any potential sensitive data from game state
                const sanitizedState = {
                    ...gameState,
                    // Ensure no sensitive data is included
                };
                scope.setExtra('gameState', sanitizedState);
            }

            // Add context tags
            scope.setTag('screen', currentScreen || 'unknown');
            scope.setTag('gamePhase', gamePhase || 'unknown');
            scope.setTag('reportType', 'user_initiated');
            scope.setTag('hasUserMessage', userMessage && userMessage.trim() ? 'yes' : 'no');

            // Add screen dimensions
            if (screenSize) {
                scope.setExtra('screenSize', screenSize);
            }

            // Add device info
            scope.setExtra('deviceInfo', {
                userAgent: navigator.userAgent,
                language: navigator.language,
                platform: navigator.platform,
                screenWidth: window.screen?.width,
                screenHeight: window.screen?.height,
                viewportWidth: window.innerWidth,
                viewportHeight: window.innerHeight,
                isNative: Capacitor.isNativePlatform(),
                nativePlatform: Capacitor.getPlatform(),
            });

            // Add full user message as extra data (always accessible)
            if (userMessage && userMessage.trim()) {
                scope.setExtra('userMessage', userMessage.trim());
            }

            // Send as a message (not an error) since this is user-initiated
            // Include user message in the title if provided (extended to 5000 chars)
            const reportTitle = userMessage && userMessage.trim()
                ? `User Error Report: ${userMessage.substring(0, 5000)}`
                : 'User Error Report';
            Sentry.captureMessage(reportTitle, 'info');
        });

        // Try to flush, but don't fail if it times out
        // The event is queued and may be sent later
        try {
            const flushed = await Sentry.flush(5000);
            if (flushed) {
                debugLog('[SentryService] Error report flushed successfully');
            } else {
                debugLog('[SentryService] Flush timed out, but event is queued');
            }
        } catch (flushError) {
            // Flush failed but event might still be queued
            console.warn('[SentryService] Flush failed, but event is queued:', flushError.message);
        }

        // Return true because the event was captured (even if flush failed)
        debugLog('[SentryService] Error report captured');
        return true;
    } catch (error) {
        console.error('[SentryService] Failed to capture error report:', error);
        return false;
    }
};

/**
 * Capture an exception with game context
 * 
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context
 */
export const captureException = (error, context = {}) => {
    if (!isInitialized) {
        console.error('[SentryService] Not initialized, logging error locally:', error);
        return;
    }

    Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) => {
            scope.setExtra(key, value);
        });
        Sentry.captureException(error);
    });
};

/**
 * Check if Sentry is initialized and working
 * @returns {boolean}
 */
export const isSentryReady = () => isInitialized;

export default {
    initSentry,
    sendErrorReport,
    captureException,
    isSentryReady,
};
